import { useState } from 'react';
import { useWallet } from '@/hooks/useWallet';
import { useLegacyStaking } from '@/hooks/useLegacyStaking';
import { LINKS } from '@/config/contracts';

interface LegacyStakingProps {
  wallet: ReturnType<typeof useWallet>;
  capsBalance: number;
}

export function LegacyStaking({ wallet, capsBalance }: LegacyStakingProps) {
  const staking = useLegacyStaking(wallet.address, wallet.signer);
  const [tab, setTab] = useState<'stake' | 'unstake'>('stake');
  const [amount, setAmount] = useState('');

  const stakedDisplay = parseFloat(staking.stakedAmount).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const capsDisplay = (capsBalance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const handleMax = () => {
    if (tab === 'stake') {
      const bal = capsBalance || 0;
      const max = Math.max(0, Math.floor(bal - 2));
      setAmount(max > 0 ? max.toString() : '0');
    } else {
      const staked = parseFloat(staking.stakedAmount);
      setAmount(staked > 0 ? Math.floor(staked).toString() : '0');
    }
  };

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) return;
    if (tab === 'stake') {
      await staking.stake(amount);
    } else {
      await staking.unstake(amount);
    }
    setAmount('');
  };

  return (
    <div className="max-w-[600px] mx-auto">
      {/* Back link */}
      <button
        onClick={() => { window.location.hash = ''; }}
        className="flex items-center gap-2 text-gray-400 hover:text-white text-sm font-display mb-6 transition-colors bg-transparent border-none cursor-pointer"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="inline-block font-display text-[11px] font-bold uppercase tracking-[0.12em] px-5 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-ternoa mb-4">
          Legacy
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">CAPS Staking</h1>
        <p className="text-sm text-gray-400 mt-2">
          Manage your staked CAPS on the legacy staking contract.
        </p>
      </div>

      {/* Staked balance card */}
      <div className="glass-card p-6 mb-6">
        <div className="text-xs font-display uppercase tracking-widest text-gray-400 mb-2">Your Staked CAPS</div>
        <div className="text-3xl font-display font-semibold text-white">
          {staking.loading ? (
            <span className="inline-block w-32 h-8 bg-white/5 rounded animate-pulse" />
          ) : (
            stakedDisplay
          )}
          <span className="text-lg text-gray-400 ml-2">CAPS</span>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          Available balance: {capsDisplay} CAPS
        </div>
      </div>

      {/* Tabs + Form */}
      <div className="glass-card p-6">
        {/* Tab switcher */}
        <div className="flex gap-1 p-1 bg-white/5 rounded-ternoa mb-6">
          {(['stake', 'unstake'] as const).map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); setAmount(''); staking.clearError(); }}
              className={`flex-1 py-2.5 text-sm font-display font-medium uppercase tracking-wider rounded-ternoa transition-all cursor-pointer border-none ${
                tab === t
                  ? 'bg-accent text-white'
                  : 'bg-transparent text-gray-400 hover:text-white'
              }`}
            >
              {t === 'stake' ? 'Stake' : 'Unstake'}
            </button>
          ))}
        </div>

        {/* Amount input */}
        <div className="mb-4">
          <label className="text-xs font-display uppercase tracking-widest text-gray-400 mb-2 block">
            Amount (CAPS)
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0"
              min="0"
              className="flex-1 bg-white/5 border border-glass-border rounded-ternoa px-4 py-3 text-white font-body text-lg outline-none focus:border-accent transition-colors"
              disabled={staking.txPending}
            />
            <button
              onClick={handleMax}
              className="px-4 py-3 text-xs font-display font-bold uppercase tracking-wider text-accent border border-accent/30 bg-accent/10 rounded-ternoa hover:bg-accent/20 transition-colors cursor-pointer"
              disabled={staking.txPending}
            >
              Max
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            {tab === 'stake'
              ? `Available to stake: ${Math.max(0, Math.floor((capsBalance || 0) - 2)).toLocaleString()} CAPS (2 CAPS reserved for gas)`
              : `Available to unstake: ${stakedDisplay} CAPS`
            }
          </div>
        </div>

        {/* Error */}
        {staking.error && (
          <div className="mb-4 p-3 rounded-ternoa bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center justify-between">
            <span>{staking.error}</span>
            <button
              onClick={staking.clearError}
              className="ml-2 text-current opacity-60 hover:opacity-100 bg-transparent border-none cursor-pointer"
            >
              x
            </button>
          </div>
        )}

        {/* Success / tx hash */}
        {staking.txHash && !staking.txPending && (
          <div className="mb-4 p-3 rounded-ternoa bg-success/10 border border-success/30 text-success text-sm">
            Transaction confirmed!{' '}
            <a
              href={LINKS.explorerTx(staking.txHash)}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:no-underline"
            >
              View on explorer
            </a>
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={staking.txPending || !amount || parseFloat(amount) <= 0}
          className="w-full btn-primary py-3 text-base font-display font-semibold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {staking.txPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {staking.txHash ? 'Confirming...' : 'Sending...'}
            </span>
          ) : (
            tab === 'stake' ? 'Stake CAPS' : 'Unstake CAPS'
          )}
        </button>
      </div>
    </div>
  );
}
